import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Sheet, Table, Typography, Alert } from '@mui/joy';
import axios from '../util/axiosConfig';
import { QUIZ_LEADERBOARD_UPDATED } from '../util/liveQuizEvents';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '420px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider',
    mt: 1.5
};

/** Normalize API payload to an array of leaderboard rows. */
const normalizeLeaderboardRows = (payload) => {
    const data = payload?.data ?? payload;
    if (Array.isArray(data)) {
        return data;
    }
    if (data && Array.isArray(data.data)) {
        return data.data;
    }
    if (data && typeof data === 'object') {
        if (data.studentid != null || data.name != null) {
            return [data];
        }
        const values = Object.values(data).filter((item) => item && typeof item === 'object');
        if (values.length > 0) {
            return values;
        }
    }
    return [];
};

const leaderboardTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
    '& tbody tr:hover': { backgroundColor: '#fff5ed' },
    '& thead': { position: 'sticky', top: 0, zIndex: 10 },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'middle',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 72, minWidth: 72, whiteSpace: 'nowrap' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 220, minWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 100, minWidth: 100, whiteSpace: 'nowrap' }
};

/**
 * Live or final quiz leaderboard for the current session.
 * @param {'live'|'final'} variant
 */
const QuizLeaderboard = ({
    sessionId,
    currentStudentId,
    variant = 'live',
    externalRows = null,
    disablePolling = false
}) => {
    const isFinal = variant === 'final';
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadLeaderboard = useCallback(async () => {
        try {
            const res = await axios.get(`/quizzes/session/${sessionId}/results`);
            if (!res?.data?.success) {
                throw new Error(res?.data?.errorMessage || 'Failed to load leaderboard');
            }
            const list = normalizeLeaderboardRows(res.data);
            setRows(Array.isArray(list) ? list : []);
            setError('');
        } catch (e) {
            setError(e?.response?.data?.errorMessage || e?.message || 'Failed to load leaderboard');
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (Array.isArray(externalRows)) {
            setRows(externalRows);
            setLoading(false);
            setError('');
            return undefined;
        }
        setLoading(true);
        loadLeaderboard();
        return undefined;
    }, [loadLeaderboard, isFinal, disablePolling, externalRows]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const onLeaderboard = (event) => {
            const detail = event?.detail;
            if (!detail || detail.sessionId !== sessionId) return;
            if (!Array.isArray(detail.leaderboard)) return;
            setRows(detail.leaderboard);
            setLoading(false);
            setError('');
        };

        window.addEventListener(QUIZ_LEADERBOARD_UPDATED, onLeaderboard);
        return () => window.removeEventListener(QUIZ_LEADERBOARD_UPDATED, onLeaderboard);
    }, [sessionId]);

    const highlightId = currentStudentId != null ? String(currentStudentId) : null;
    const tableRows = Array.isArray(rows) ? rows : [];
    const myIndex = highlightId
        ? tableRows.findIndex((row) => String(row.studentid) === highlightId)
        : -1;
    const myRow = myIndex >= 0 ? tableRows[myIndex] : null;
    const myRank = myIndex >= 0 ? myIndex + 1 : null;
    const isTopThree = myRank != null && myRank <= 3;

    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography level="title-md" sx={{ mb: 0.5 }}>
                {isFinal ? 'Final results' : 'Leaderboard'}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {isFinal
                    ? 'Full standings for this quiz session.'
                    : 'Rankings update live. The next question will appear here when your faculty starts it.'}
            </Typography>

            {isFinal && myRow && (
                <Sheet
                    variant="soft"
                    color="warning"
                    sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 'sm',
                        border: '1px solid',
                        borderColor: '#ea7a23'
                    }}
                >
                    <Typography level="title-sm" sx={{ color: '#e55125' }}>
                        {isTopThree ? 'Congratulations!' : 'Thanks for participating.'}
                    </Typography>
                    {isTopThree && (
                        <Typography level="body-sm" sx={{ mt: 0.25, color: 'text.secondary' }}>
                            You are in position <strong>{myRank}</strong>.
                        </Typography>
                    )}
                    <Typography level="body-md" sx={{ mt: 0.5 }}>
                        Rank <strong>{myRank}</strong> of {tableRows.length}
                        {' · '}
                        Score <strong>{Number(myRow.totalScore || 0)}</strong>
                    </Typography>
                </Sheet>
            )}

            {error && (
                <Alert color="danger" variant="soft" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}

            {loading && tableRows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size="sm" />
                </Box>
            ) : tableRows.length === 0 ? (
                <Typography level="body-sm" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    No participants yet.
                </Typography>
            ) : (
                <Sheet variant="outlined" sx={tableSheetSx}>
                    <Table stickyHeader sx={leaderboardTableSx}>
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Name</th>
                                <th>Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.map((row, index) => {
                                const rank = index + 1;
                                const isYou = highlightId && String(row.studentid) === highlightId;
                                return (
                                    <tr
                                        key={`${row.studentid}-${index}`}
                                        style={isYou ? { backgroundColor: '#ffe8d4', fontWeight: 600 } : undefined}
                                    >
                                        <td>{rank}</td>
                                        <td>
                                            {row.name || row.studentid}
                                            {isYou && (
                                                <Typography component="span" level="body-xs" sx={{ ml: 0.5, color: '#ea7a23' }}>
                                                    (You)
                                                </Typography>
                                            )}
                                        </td>
                                        <td>{Number(row.totalScore || 0)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </Box>
    );
};

export default QuizLeaderboard;
